import moment, {Moment} from 'moment-timezone';
import {DateTimeRangeTuple, Interval, LocalInterval, LocalDateTimeRangeTuple} from '@app/graphql-typings/graphql';

type CeilDateType = 'inclusive-date' | 'exclusive-date' | 'inclusive-date-time' | 'exclusive-date-time';

export class MomentUtil {
  static INTERVAL_PARTS_SEPARATOR = '/';
  static TIMESTAMP_WITHOUT_TIMEZONE_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSS';

  private static readonly SUB_DAY_UNITS = new Set<moment.unitOfTime.Base>(['hour', 'minute', 'second', 'millisecond']);

  static isStartOfMonth(date: Moment): boolean {
    return date.isSame(date.clone().startOf('month').startOf('day'));
  }

  static isEndOfMonth(date: Moment): boolean {
    return date.isSame(date.clone().endOf('month').startOf('day'));
  }

  static rangeToInterval(range: DateTimeRangeTuple): Interval {
    return range.map(x => x.toISOString()).join(MomentUtil.INTERVAL_PARTS_SEPARATOR);
  }

  static rangeToLocalInterval(range: LocalDateTimeRangeTuple): LocalInterval {
    return range.map(d => d.format(MomentUtil.TIMESTAMP_WITHOUT_TIMEZONE_FORMAT)).join(MomentUtil.INTERVAL_PARTS_SEPARATOR);
  }

  static isToday(date: Moment): boolean {
    return date?.isSame(moment.tz(date.tz()), 'date') ?? false;
  }

  static today(tz?: string): Moment {
    return (tz ? moment.tz(tz) : moment()).startOf('date');
  }

  static ceil(date: Moment, unit: moment.unitOfTime.Base, dateType: CeilDateType): Moment {
    const isDate = dateType === 'inclusive-date' || dateType === 'exclusive-date';
    if (isDate && MomentUtil.SUB_DAY_UNITS.has(unit)) {
      throw new Error(`Unsupported sub-day unit for a 'date' ceil: ${unit}`);
    }
    if (dateType === 'inclusive-date' || dateType === 'inclusive-date-time') {
      const endOfPeriod = date.clone().endOf(unit);
      return isDate ? endOfPeriod.startOf('day') : endOfPeriod;
    } else {
      const floored = MomentUtil.floor(date, unit);
      return floored.isSame(date) ? floored : floored.add(1, unit);
    }
  }

  static floor(date: Moment, unit: moment.unitOfTime.Base): Moment {
    return date.clone().startOf(unit);
  }
}
